import { Box, InputLabel, InputLabelProps, OutlinedInput, SelectProps } from '@mui/material';
import {
  ForwardedRef,
  forwardRef,
  memo,
  SyntheticEvent,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'react-intl';

import { phrasesMessages } from '../../translations/phrases.messages';
import { HBIcon, HBIconType } from '../HBIcon/HBIcon';
import { HBTextField } from '../HBTextField/HBTextField';
import {
  HBSelectTreeFormControlStyle,
  HBSelectTreeRootStyle,
  HBSelectTreeSearchStyle,
  HBSelectTreeViewStyle,
} from './HBSelectTree.styles';
import HBSelectTreeItem from './HBSelectTreeItem';
import HBSelectTreeRenderValue from './HBSelectTreeRenderValue';
import { flatListToTreeList, searchTree } from './utils';
import { TestIds } from '@/core/constants/test-ids';

export type HBSelectTreeDataProps = {
  id: string;
  parentId?: string | null;
  selectable?: boolean;
  label: string;
  parentSelectable?: boolean;
  value: string;
  children?: HBSelectTreeDataProps[];
  isAllocatableToProduct?: boolean;
};

let doFilter: NodeJS.Timeout;

export type ReturnValueType = { value: string; label: string };

export interface HBSelectTreeProps extends Omit<SelectProps, 'ref' | 'children' | 'onChange'> {
  data: HBSelectTreeDataProps[];
  onSelectParent?: (parentId: string) => void;
  onSelectChild?: (childId: string) => void;
  onSearch?: (search?: string) => void;
  searchPlaceholder?: string;
  selectChildrenWithParent?: boolean;
  renderValueEmptyLabel?: string;
  defaultValue?: HBSelectTreeDataProps | null;
  collapseIcon?: HBIconType;
  expandIcon?: HBIconType;
  rootParentValue?: string | number | null | undefined;
  onChange?: (value: string | any[]) => void;
  inputLabelProps?: InputLabelProps;
  label?: string;
  selectTreeViewStyle?: Record<string, unknown>;
  defaultSearchValue?: string;
  valueWithAllData?: boolean;
}

const HBSelectTree = forwardRef(
  <T extends HTMLSelectElement>(props: HBSelectTreeProps, ref: ForwardedRef<T>) => {
    const { formatMessage } = useIntl();
    const {
      data,
      onSelectParent,
      onSelectChild,
      selectChildrenWithParent = false,
      defaultValue,
      onSearch,
      multiple,
      searchPlaceholder = formatMessage(phrasesMessages.search),
      renderValueEmptyLabel = formatMessage(phrasesMessages.selectItem),
      collapseIcon = 'angleDown',
      expandIcon = 'angleLeft',
      inputLabelProps,
      onChange,
      selectTreeViewStyle,
      defaultSearchValue,
      valueWithAllData,
      label,
      sx,
      style,
      fullWidth,
      size = 'small',
      required,
      disabled,
      ...selectProps
    } = props;

    const isValueFromProps = useRef(false);
    const [tree, setTree] = useState<HBSelectTreeDataProps[]>([]);
    const [searchValue, setSearchValue] = useState<string>(defaultSearchValue || '');
    const [open, setOpen] = useState(false);
    const [treeItemSelect, setTreeItemSelect] = useState<HBSelectTreeDataProps[]>([]);
    const [selectValue, setSelectValue] = useState<string | string[]>(multiple ? [] : '');
    useEffect(() => {
      setTree(flatListToTreeList([...data]));
      if (data.length) {
        const value = selectProps?.value as string | string[];
        if (value) {
          isValueFromProps.current = true;
          // eslint-disable-next-line @typescript-eslint/no-unused-expressions
          !multiple || typeof value === 'string'
            ? setSelectValue(value)
            : setSelectValue([...value]);
          const treeItems = data.filter((item) =>
            !multiple
              ? value === item.value
              : (value as string[]).findIndex(
                  (data: string | ReturnValueType) =>
                    (valueWithAllData ? (data as ReturnValueType)?.value : data) === item.value,
                ) !== -1,
          );
          setTreeItemSelect(treeItems);
        }
      }
    }, [data, selectProps?.value]);

    useEffect(() => {
      if (defaultSearchValue) {
        setSearchValue(defaultSearchValue);
      }
    }, [defaultSearchValue]);

    const handleClickTreeItem = useCallback(
      (selectedItem: HBSelectTreeDataProps, event?: React.ChangeEvent<HTMLInputElement>) => {
        const filterSelectableItems = (itemsToFilter: string | any[]) => {
          if (typeof itemsToFilter === 'string') {
            return itemsToFilter;
          }

          const selectableItems = itemsToFilter?.filter((item: string | ReturnValueType) => {
            const matchingData = data.find(
              (dataItem) =>
                dataItem.value === (valueWithAllData ? (item as ReturnValueType).value : item),
            );
            if (matchingData) {
              return matchingData.selectable;
            }
            return false;
          });

          return selectableItems;
        };

        const { checked } = event?.target || {};
        const children = data.filter((child) => child.parentId === selectedItem.id);
        isValueFromProps.current = false;

        if (typeof checked !== 'boolean') {
          return;
        }

        const nonCheckedValues = (
          !multiple
            ? ''
            : (prevSelectedValues: string[]) => {
                const newValue = prevSelectedValues.filter(
                  (value: string | ReturnValueType) =>
                    (valueWithAllData ? (value as ReturnValueType).value : value) !==
                    selectedItem.value,
                );
                if (selectChildrenWithParent) {
                  onChange?.(filterSelectableItems(newValue));
                } else {
                  onChange?.(newValue);
                }
                return newValue;
              }
        ) as string | string[];

        const nonCheckedTreeItemSelectValues = !multiple
          ? []
          : (prevSelectedItems: HBSelectTreeDataProps[]) =>
              prevSelectedItems.filter((item) => item.id !== selectedItem.id);

        const isParent = selectedItem.parentSelectable
          ? false
          : data.filter((item) => item.parentId === selectedItem.id).length > 0;
        const checkedValues = (
          !multiple
            ? selectedItem.value
            : selectValue.includes(selectedItem.value)
              ? (selectValue as string[]).filter((value: string) => value !== selectedItem.value)
              : (prevSelectedValues: typeof selectValue) => {
                  const newValue = [
                    ...prevSelectedValues,
                    valueWithAllData ? selectedItem : selectedItem?.value,
                  ];
                  if (selectChildrenWithParent) {
                    onChange?.(filterSelectableItems(newValue as any));
                  } else {
                    onChange?.(newValue);
                  }
                  return newValue;
                }
        ) as string[] | string;
        if (!isParent) {
          if (!multiple) {
            onChange?.(checked ? checkedValues : nonCheckedValues);
          }

          setSelectValue(checked ? checkedValues : nonCheckedValues);

          const checkedTreeItemSelectValues = !multiple
            ? [selectedItem]
            : treeItemSelect.find((item) => item.id === selectedItem.id)
              ? treeItemSelect.filter((item) => item.id !== selectedItem.id)
              : (prevSelectedItems: HBSelectTreeDataProps[]) => [
                  ...prevSelectedItems,
                  selectedItem,
                ];

          setTreeItemSelect(checked ? checkedTreeItemSelectValues : nonCheckedTreeItemSelectValues);

          if (!multiple) {
            onSelectChild?.(selectedItem.id);
            setOpen(false);
          }
        } else {
          onSelectParent?.(selectedItem.id);
          if (!multiple) {
            onChange?.(checked ? checkedValues : nonCheckedValues);
          }
          setSelectValue(checked ? checkedValues : nonCheckedValues);
        }

        if (selectChildrenWithParent) {
          children?.forEach((child) => {
            handleClickTreeItem(child, {
              target: {
                checked,
              },
            } as unknown as React.ChangeEvent<HTMLInputElement>);
          });
        }
      },
      [
        data,
        multiple,
        onChange,
        onSelectChild,
        onSelectParent,
        selectValue,
        setSelectValue,
        treeItemSelect,
        selectChildrenWithParent,
      ],
    );

    const handleClose = (event: SyntheticEvent) => {
      setOpen(false);
      selectProps.onClose?.(event);
      setSearchValue('');
    };

    const handleOpen = () => {
      setOpen(true);
    };

    const handleSearch = (search: string) => {
      clearTimeout(doFilter);
      setSearchValue(search);

      if (onSearch) {
        onSearch?.(search);
      } else {
        doFilter = setTimeout(() => {
          if (search.length > 0) {
            const dataTree = searchTree([...data], search);
            setTree(flatListToTreeList(dataTree));
          } else {
            setTree(flatListToTreeList([...data]));
          }
        }, 500);
      }
    };

    useEffect(() => {
      if (!selectProps?.value) {
        if (!multiple) {
          setSelectValue('');
        } else {
          setSelectValue([]);
        }
        setTreeItemSelect([]);
      }
    }, [defaultValue, selectProps?.value]);

    const generatedId = useId();
    const labelId = selectProps.labelId ?? `${selectProps.name ?? generatedId}-label`;
    const hasValue = multiple ? selectValue.length > 0 : selectValue !== '';
    const isLabelShrunk = open || hasValue;

    return (
      <HBSelectTreeFormControlStyle
        style={style}
        sx={sx}
        size={size}
        fullWidth={fullWidth}
        required={required}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
        }}
      >
        <InputLabel id={labelId} disabled={disabled} {...inputLabelProps} shrink={isLabelShrunk}>
          {label}
        </InputLabel>
        <HBSelectTreeRootStyle
          ref={ref}
          variant="outlined"
          {...selectProps}
          disabled={disabled}
          fullWidth={fullWidth}
          id={selectProps.id ?? `${selectProps.name ?? generatedId}-select`}
          label={isLabelShrunk ? label : undefined}
          labelId={labelId}
          multiple={multiple}
          open={open}
          input={
            <OutlinedInput
              size={size}
              label={isLabelShrunk ? label : undefined}
              notched={isLabelShrunk}
            />
          }
          onClose={handleClose}
          onOpen={handleOpen}
          size={size}
          value={selectValue}
          displayEmpty
          renderValue={(value) => {
            return (
              <HBSelectTreeRenderValue
                value={value}
                treeItemSelect={treeItemSelect}
                label={renderValueEmptyLabel}
              />
            );
          }}
          inputProps={{
            ...selectProps.inputProps,
            'data-test-id': TestIds.core.selectInput,
          }}
          data-test-id={TestIds.core.selectInput}
        >
          <Box>
            <HBSelectTreeSearchStyle>
              <HBTextField
                value={searchValue}
                placeholder={searchPlaceholder}
                onChange={(e) => handleSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <Box sx={{ mx: 2, mt: 1, color: (theme) => theme.palette.grey[300] }}>
                      <HBIcon type="searchAlt" />
                    </Box>
                  ),
                }}
                fullWidth
              />
            </HBSelectTreeSearchStyle>
            <HBSelectTreeViewStyle
              sx={{
                ...(selectTreeViewStyle ? selectTreeViewStyle : {}),
              }}
            >
              <HBSelectTreeItem
                tree={tree}
                onClick={handleClickTreeItem}
                selectedIds={treeItemSelect.map((item) => item.id)}
                defaultCollapseIcon={<HBIcon type={collapseIcon} size="medium" />}
                defaultExpandIcon={<HBIcon type={expandIcon} size="medium" />}
              />
            </HBSelectTreeViewStyle>
          </Box>
        </HBSelectTreeRootStyle>
      </HBSelectTreeFormControlStyle>
    );
  },
);

HBSelectTree.displayName = 'HBSelectTree';

export default memo(HBSelectTree);
